/* eslint-disable react/no-array-index-key */
import { useState } from 'react';

import {
  useFormik,
} from 'formik';
import * as yup from 'yup';
import {
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  FormHelperText,
  Link,
  Box,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useUserSignupMutation } from '../../../../../graphql/generated/graphql';

import { UserRegisterForm } from '../../../../config/interfaces';
import HaveAccount from '../../../components/onbording/have-account';
import MsgVerify from '../../../components/onbording/msg-verify';
import OnBoardingLayout from '../../../../theme/templates/on-boarding';
import JobTitle from '../../../components/job-title';

export default function RegisterPersonPage() {

  const [signup] = useUserSignupMutation();
  const [signUp, setSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const [email, setEmail] = useState('');

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    phone: '',
    password: '',
    confirmPassword: '',
    consentTextMessages: false,
    acceptPrivacyPolicy: false,
  } as UserRegisterForm & {
    consentTextMessages: boolean;
    acceptPrivacyPolicy: boolean;
  };

  const RegisterPersonSchema = yup.object().shape({
    firstName: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    lastName: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    email: yup.string()
      .email('Invalid email')
      .required('Required'),
    jobTitle: yup.string()
      .required('Required')
      .nullable(),
    phone: yup.string()
      .min(6, 'Too Short!')
      .max(12, 'Too Long!')
      .required('Required'),
    password: yup.string()
      .min(8, 'Too Short!')
      .required('Required'),
    confirmPassword: yup.string()
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
    consentTextMessages: yup.boolean()
      .oneOf([true], 'You must agree to receive text messages.'),
    acceptPrivacyPolicy: yup.boolean()
      .oneOf([true], 'You must accept the Privacy Policy.'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: RegisterPersonSchema,
    onSubmit: async (variables) => {
      setLoader(true);
      setEmail(variables.email);
      try {
        await signup({
          variables: {
            firstName: variables.firstName,
            lastName: variables.lastName,
            email: variables.email,
            jobTitle: variables.jobTitle,
            phone: variables.phone.toString(),
            password: variables.password,
            confirmPassword: variables.confirmPassword,
          },
        });
        setSignUp(true);
      } catch (e) {
        setSignUp(false);
      }
      setLoader(false);
    },
  });

  return (
    <OnBoardingLayout>
      <Grid
        sx={{
          maxWidth: '90%',
          width: '500px',
          mb: 2,
          mx: 4,
          py: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        item
      >
        <Typography
          component="h1"
          variant="h3"
          sx={{
            mb: 6,
          }}
        >
          Welcome!
        </Typography>

        {signUp ? <MsgVerify email={email} /> : (
          <>
            <FormControl
              fullWidth
              component="form"
              onSubmit={formik.handleSubmit}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="first-name"
                label="First Name"
                name="firstName"
                autoComplete="first-name"
                autoFocus
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="last-name"
                label="Last Name"
                name="lastName"
                autoComplete="last-name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <FormControl
                sx={{
                  mt: 1,
                }}
                fullWidth
              >
                <JobTitle
                  value={formik.values.jobTitle}
                  formik={formik}
                  error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
                  helperText={formik.touched.jobTitle && formik.errors.jobTitle}
                />
              </FormControl>
              <TextField
                margin="normal"
                required
                fullWidth
                name="phone"
                label="Phone"
                type="number"
                id="phone"
                autoComplete="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                sx={{
                  mb: 3,
                }}
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirm-password"
                autoComplete="confirm-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
              <FormControl
                sx={{ mt: 1 }}
                error={formik.touched.consentTextMessages && Boolean(formik.errors.consentTextMessages)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Checkbox
                    id="consentTextMessages"
                    name="consentTextMessages"
                    checked={formik.values.consentTextMessages}
                    onChange={formik.handleChange}
                  />
                  <Typography variant="body2" component="label" htmlFor="consentTextMessages">
                    I agree to receive text messages to my mobile number for communication regarding my upcoming meetings.
                  </Typography>
                </Box>
                {formik.touched.consentTextMessages && formik.errors.consentTextMessages && (
                  <FormHelperText>{formik.errors.consentTextMessages}</FormHelperText>
                )}
              </FormControl>
              <FormControl
                sx={{ mt: 1 }}
                error={formik.touched.acceptPrivacyPolicy && Boolean(formik.errors.acceptPrivacyPolicy)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Checkbox
                    id="acceptPrivacyPolicy"
                    name="acceptPrivacyPolicy"
                    checked={formik.values.acceptPrivacyPolicy}
                    onChange={formik.handleChange}
                  />
                  <Typography variant="body2" component="label" htmlFor="acceptPrivacyPolicy">
                    I accept the <Link
                      href="/privacy-policy"
                      target="_blank"
                      sx={{ color: 'secondary.main', textDecoration: 'underline' }}
                    >
                      Privacy Policy
                    </Link>.
                  </Typography>
                </Box>
                {formik.touched.acceptPrivacyPolicy && formik.errors.acceptPrivacyPolicy && (
                  <FormHelperText>{formik.errors.acceptPrivacyPolicy}</FormHelperText>
                )}
              </FormControl>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {loader ? <CircularProgress color="primary" /> : 'Register'}
              </Button>
            </FormControl>
            <HaveAccount />
          </>
        )}
      </Grid>
    </OnBoardingLayout>
  );
}